/**
 * useDashboardState
 *
 * Computes what the Dashboard hero should say right now. Two modes:
 *
 * 1. Activation mode — first-time / pre-PMF users see the step-based wizard
 *    (verify → heatmap → funnel → split-test). Generic "next best action."
 *
 * 2. Steady mode — returning power users see a state-driven hero variant
 *    derived from their actual account activity. Priority cascade fires the
 *    most decision-forcing finding first (test ready to call > test stalled >
 *    fallback weekly digest). Variants computed from real data — no placeholder
 *    copy ever surfaces for mature users.
 *
 * Variant priority (highest first):
 *   1. SIG_REACHED      — a test hit ≥95% statistical significance
 *   2. TEST_STALLED     — active test exists with 0 sessions and >48h age
 *   3. TOP_MOVER        — a single site spiking >20% WoW (Phase 3, data-gated)
 *   4. FUNNEL_DROP      — biggest computed drop-off step (Phase 3, data-gated)
 *   5. FALLBACK         — "N views today across M sites — steady pace."
 *
 * Mature account detection short-circuits the activation wizard. Mature =
 * paid plan, active trial, any test, multiple sites, OR >100 events.
 */

import { useMemo } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSavedFunnels } from '@/hooks/useAnalytics'
import { useExperiments } from '@/hooks/useExperiments'
import type { Experiment } from '@/hooks/useExperiments'

type DashboardStep =
  | 'verify'
  | 'heatmap'
  | 'funnel'
  | 'split-test'
  | 'steady'

type HeroVariant =
  | 'sig-reached'
  | 'test-stalled'
  | 'top-mover'
  | 'funnel-drop'
  | 'revenue-anomaly'
  | 'fallback'
  | 'activation' // mirrors `step` when in activation mode

interface HeroVariantData {
  variant: HeroVariant
  /** The experiment driving the hero, when applicable. */
  experiment?: Experiment
  /** The site driving the hero, when applicable. */
  site?: { propertyId: string; name: string; domain: string; deltaPct?: number }
  /** Funnel drop-off info, when applicable. */
  funnelDrop?: { from: string; to: string; deltaPct: number; viewsFrom?: number; viewsTo?: number }
  /** Revenue WoW info for the REVENUE_ANOMALY variant. */
  revenue?: { thisWeek: number; lastWeek: number; deltaPct: number; topContributorName?: string; topContributorSharePct?: number }
  /** Aggregate counts for the fallback variant. */
  fallback?: { viewsToday: number; siteCount: number; activeTestCount: number; readyToCallCount: number }
}

interface DashboardState {
  step: DashboardStep
  /** Numeric step for the progress bar (1-5). `steady` shows a full bar. */
  stepNumber: number
  totalSteps: number
  /** True when the user has no tracking activity yet. */
  isFreshAccount: boolean
  hasEvents: boolean
  hasFunnel: boolean
  hasSplitTest: boolean
  isLoading: boolean
  /** Steady-mode hero variant + the data it needs to render. */
  hero: HeroVariantData
  /** Count of active (running) experiments. */
  activeTestCount: number
  /** Count of tests that have hit significance and are ready to be called. */
  readyToCallCount: number
}

interface Options {
  propertyId?: string | null
  totalEvents?: number
  hasPaidPlan?: boolean
  hasActiveTrial?: boolean
  siteCount?: number
  /** Aggregate views today across all sites (drives FALLBACK variant). */
  viewsToday?: number
  /** Optional per-site WoW deltas, keyed by propertyId. When provided, the
   *  TOP_MOVER variant can fire. */
  siteDeltas?: Record<string, number>
  /** Sites array for resolving TOP_MOVER metadata. */
  sites?: Array<{ propertyId: string; name: string; domain: string }>
  /** Biggest computed funnel drop. Enables FUNNEL_DROP variant. */
  funnelDrop?: { from: string; to: string; deltaPct: number }
  /** Aggregate revenue WoW signal. Enables REVENUE_ANOMALY variant. */
  revenue?: {
    thisWeek: number
    lastWeek: number
    deltaPct: number
    topContributorName?: string
    topContributorSharePct?: number
  }
}

const TOTAL_STEPS = 5
const MATURE_EVENT_THRESHOLD = 100
const SIGNIFICANCE_THRESHOLD = 95
const TOP_MOVER_DELTA_THRESHOLD = 20
const REVENUE_DELTA_THRESHOLD = 20
const FUNNEL_DROP_THRESHOLD = 30
const STALLED_AGE_MS = 48 * 60 * 60 * 1000
// Tests dead longer than 30 days are abandoned cruft, not actionable urgency.
// They get surfaced via the Active Tests card's sweep UI instead of the hero —
// otherwise a forgotten dev test from 18 months ago outranks today's real signals.
const STALLED_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

function isStalled(test: Experiment): boolean {
  if (test.status !== 'active') return false
  if ((test.totalSessions ?? 0) > 0) return false
  if (!test.createdAt) return false
  const age = Date.now() - new Date(test.createdAt).getTime()
  return age > STALLED_AGE_MS && age <= STALLED_MAX_AGE_MS
}

function hasReachedSig(test: Experiment): boolean {
  return (test.statisticalSignificance ?? 0) >= SIGNIFICANCE_THRESHOLD
}

export function useDashboardState({
  propertyId,
  totalEvents = 0,
  hasPaidPlan = false,
  hasActiveTrial = false,
  siteCount = 0,
  viewsToday = 0,
  siteDeltas,
  sites,
  funnelDrop,
  revenue,
}: Options): DashboardState {
  const { token } = useAuth()

  const { data: savedFunnels } = useSavedFunnels({
    propertyId: propertyId ?? undefined,
  })
  const { data: experiments } = useExperiments({
    propertyId: propertyId ?? undefined,
    token: token ?? undefined,
  })

  return useMemo<DashboardState>(() => {
    const hasEvents = totalEvents > 0
    const hasFunnel = Array.isArray(savedFunnels) && savedFunnels.length > 0
    const expArray: Experiment[] = Array.isArray(experiments) ? experiments : []
    const hasSplitTest = expArray.length > 0
    const isLoading = !propertyId

    const isMature =
      hasSplitTest ||
      hasPaidPlan ||
      hasActiveTrial ||
      siteCount > 1 ||
      totalEvents >= MATURE_EVENT_THRESHOLD

    // ── Activation funnel (new users) ────────────────────────────────────────
    let step: DashboardStep = 'verify'
    let stepNumber = 1

    if (isMature) {
      step = 'steady'
      stepNumber = TOTAL_STEPS
    } else if (!hasEvents) {
      step = 'verify'
      stepNumber = 1
    } else if (!hasFunnel) {
      step = 'heatmap'
      stepNumber = 2
    } else if (!hasSplitTest) {
      step = 'funnel'
      stepNumber = 3
    } else {
      step = 'steady'
      stepNumber = TOTAL_STEPS
    }

    // ── Aggregate test status (drives multiple variants + the Active Tests card) ─
    const activeTests = expArray.filter((e) => e.status === 'active')
    const readyToCall = expArray.filter((e) => e.status !== 'archived' && hasReachedSig(e))
    const stalledTests = activeTests.filter(isStalled)

    // ── Variant cascade — only fires for steady-state users ──────────────────
    //
    // Priority (Halbert: "the lead is everything" — money/urgency wins):
    //   1. SIG_REACHED        — a test hit ≥95% (decision-forcing, no $ gate)
    //   2. REVENUE_ANOMALY    — revenue moved ≥20% WoW (Stripe-gated, $ urgency)
    //   3. FUNNEL_DROP        — biggest leak ≥30% (revenue-adjacent urgency)
    //   4. TOP_MOVER          — site spiking ≥20% WoW (discovery signal)
    //   5. TEST_STALLED       — active test, 0 sessions in 48h (housekeeping)
    //   6. FALLBACK           — weekly digest (never "Pick up where you left off")
    let hero: HeroVariantData

    const fallback: HeroVariantData = {
      variant: 'fallback',
      fallback: {
        viewsToday,
        siteCount,
        activeTestCount: activeTests.length,
        readyToCallCount: readyToCall.length,
      },
    }

    if (step !== 'steady') {
      hero = { variant: 'activation' }
    } else if (readyToCall.length > 0) {
      // SIG_REACHED — pick the most-significant significance-hitter
      const top = [...readyToCall].sort((a, b) =>
        (b.statisticalSignificance ?? 0) - (a.statisticalSignificance ?? 0)
      )[0]
      hero = { variant: 'sig-reached', experiment: top }
    } else if (revenue && Math.abs(revenue.deltaPct) >= REVENUE_DELTA_THRESHOLD) {
      hero = { variant: 'revenue-anomaly', revenue }
    } else if (funnelDrop && funnelDrop.deltaPct >= FUNNEL_DROP_THRESHOLD) {
      hero = { variant: 'funnel-drop', funnelDrop }
    } else if (siteDeltas && sites && sites.length > 0) {
      // TOP_MOVER — biggest WoW spike across sites
      const ranked = sites
        .map((s) => ({ ...s, delta: siteDeltas[s.propertyId] ?? 0 }))
        .filter((s) => Math.abs(s.delta) >= TOP_MOVER_DELTA_THRESHOLD)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      if (ranked.length > 0) {
        const top = ranked[0]
        hero = {
          variant: 'top-mover',
          site: { propertyId: top.propertyId, name: top.name, domain: top.domain, deltaPct: top.delta },
        }
      } else if (stalledTests.length > 0) {
        const top = [...stalledTests].sort((a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )[0]
        hero = { variant: 'test-stalled', experiment: top }
      } else {
        hero = fallback
      }
    } else if (stalledTests.length > 0) {
      const top = [...stalledTests].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )[0]
      hero = { variant: 'test-stalled', experiment: top }
    } else {
      hero = fallback
    }

    return {
      step,
      stepNumber,
      totalSteps: TOTAL_STEPS,
      isFreshAccount: !hasEvents,
      hasEvents,
      hasFunnel,
      hasSplitTest,
      isLoading,
      hero,
      activeTestCount: activeTests.length,
      readyToCallCount: readyToCall.length,
    }
  }, [
    propertyId,
    totalEvents,
    hasPaidPlan,
    hasActiveTrial,
    siteCount,
    viewsToday,
    siteDeltas,
    sites,
    funnelDrop,
    revenue,
    savedFunnels,
    experiments,
  ])
}
